import { memo } from 'react';
import { PublicationDetailProps } from '../../interfaces/components';
import { usePublicationDetail } from './hooks/usePublicationDetail';
import { usePublicationLogic } from './hooks/usePublicationLogic';
import { useResponsiveSlider } from './hooks/useResponsiveSlider';
import { useWindowSize } from 'use-hooks';
import { Chip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

// Componentes refactorizados
import PublicationHeader from './components/PublicationHeader/PublicationHeader';
import PublicationImageSlider from './components/PublicationImageSlider/PublicationImageSlider';
import PublicationCharacteristics from './components/PublicationCharacteristics/PublicationCharacteristics';
import PublicationDescription from './components/PublicationDescription/PublicationDescription';
import PublicationInformation from './components/PublicationInformation/PublicationInformation';
import PublicationSimilars from './components/PublicationSimilars/PublicationSimilars';
import PublicationPromoBanners from './components/PublicationPromoBanners/PublicationPromoBanners';

// Componentes existentes
import CarBrandCharacteristics from '../CarBrandCharacteristics/CarBrandCharacteristics';
import ModalCharacteristics from './components/ModalCharacteristics/ModalCharacteristics';

import styles from './publicationDetail.module.scss';
import { PublicationPriceIndicator } from './components';
import ButtonShared from '../Button/Button';
import { images } from '../../constants/images';
import PublicationVersionSelector from './components/PublicationVersionSelector/PublicationVersionSelector';
import PublicationDealershipSelector from './components/PublicationDealershipSelector/PublicationDealershipSelector';

const PublicationDetailRefactored = ({
    type = "publication",
    publication,
    loading = false,
    clickFavorite = () => { },
    publications,
    children,
    buttonOff = false,
    handleDealerLogoClick,
    logoDealerships,
    onLinzeCheckClick,
    onAutoFinClick,
    vehicleTypeSelected,
    setVehicleTypeReducer,
    handlePressCotizar,
    brand,
    brandsDealerships,
    selectedDealership,
    setSelectedDealership,
    selectedVersion,
    setSelectedVersion
}: PublicationDetailProps) => {

    // Hooks personalizados
    const hookData = usePublicationDetail({ publication });
    const { sliderLimit } = useResponsiveSlider();
    const windowSize = useWindowSize();
    const {
        getMinPrice,
        selectedVehicleData,
        openAdditionalModal,
        setOpenAdditionalModal,
    } = usePublicationLogic({
        publication,
        brandsDealerships,
        selectedDealership,
        setSelectedDealership,
        selectedVersion,
        setSelectedVersion,
        setVehicleTypeReducer
    });

    const {
        isBonus,
        formData,
        selectedFeatures,
        getIndicatorPositionFromSuggested,
        getPriceDifferenceMessage,
        getIndicatorPosition,
        getPriceCategory,
        getPriceCategoryPublication,
        getIndicatorPositionPublication,
        hasValidSuggestedPrices,
        hasValidSuggestedPricesPublication
    } = publication && !loading ? hookData : {
        isBonus: false,
        formData: null,
        selectedFeatures: [],
        getIndicatorPositionFromSuggested: () => 0,
        getPriceDifferenceMessage: () => "",
        getIndicatorPosition: () => 0,
        getIndicatorPositionPublication: () => 0,
        getPriceCategory: () => "fair" as const,
        getPriceCategoryPublication: () => "fair" as const,
        hasValidSuggestedPrices: false,
        hasValidSuggestedPricesPublication: false
    };
    
    return (
        <>
            <PublicationHeader
                vehicleTypeSelected={vehicleTypeSelected}
                publication={publication}
                brand={brand}
                type={type}
                selectedVersion={selectedVersion}
                setSelectedVersion={setSelectedVersion}
                loading={loading}
                children={children}
                logoDealerships={logoDealerships}
                handleDealerLogoClick={handleDealerLogoClick}
                onLinzeCheckClick={onLinzeCheckClick}
                onAutoFinClick={onAutoFinClick}
                brandsDealerships={brandsDealerships}
                selectedDealership={selectedDealership}
                setSelectedDealership={setSelectedDealership}
                setOpenAdditionalModal={setOpenAdditionalModal}
            />

            <div className={styles.mainContent}>
                {/* Columna izquierda */}
                <div className={styles.leftColumn}>
                    <div className={brand ? styles.leftBoxBrand : styles.leftBox}>
                        <PublicationImageSlider
                            type={type}
                            brand={brand}
                            publication={publication}
                            selectedVersion={selectedVersion}
                            onLinzeCheckClick={onLinzeCheckClick}
                            vehicleTypeSelected={vehicleTypeSelected}
                            loading={loading}
                        />

                        {brand && (
                            <>
                                <CarBrandCharacteristics
                                    publication={publication}
                                    selectedVersion={selectedVersion}
                                />
                                <div
                                    className={styles.additionalCharacteristicsLink}
                                    onClick={() => setOpenAdditionalModal(true)}
                                >
                                    Ver características adicionales
                                </div>
                                <ModalCharacteristics
                                    open={openAdditionalModal}
                                    onClose={() => setOpenAdditionalModal(false)}
                                    selectedVehicleId={selectedVersion || ''}
                                    publication={publication}
                                />
                            </>
                        )}

                        {!brand && (
                            <PublicationCharacteristics
                                publication={publication}
                                loading={loading}
                            />
                        )}

                        {!brand && (
                            <PublicationDescription
                                type={type}
                                publication={publication}
                                loading={loading}
                                selectedFeatures={selectedFeatures}
                            />
                        )}
                    </div>
                </div>

                {/* Columna derecha */}
                <div className={styles.rightColumn}>
                    <div className={styles.rightBox}>
                        <PublicationInformation
                            publication={publication}
                            loading={loading}
                            logoDealerships={logoDealerships}
                            handleDealerLogoClick={handleDealerLogoClick}
                            brand={brand}
                            type={type}
                            vehicleTypeSelected={vehicleTypeSelected}
                            getMinPrice={getMinPrice}
                            hasValidSuggestedPricesPublication={hasValidSuggestedPricesPublication}
                            getPriceCategoryPublication={type === "salePost" ? getPriceCategory : getPriceCategoryPublication}
                            selectedVehicleData={selectedVehicleData}
                            selectedVersion={selectedVersion}
                        >
                            {type === "myPublication" ? children : null}
                        </PublicationInformation>

                        {type !== "myPublication" && type !== "salePost" && vehicleTypeSelected === "used" && (
                            <div className={publication?.detail ? styles.actionsButtons : styles.actionsButtonsNoDetail}>
                                {children}
                            </div>
                        )}
                        {type !== "myPublication" && type !== "salePost" && vehicleTypeSelected === "new" && (
                            <div className={publication?.detail ? styles.actionsButtons : styles.actionsButtonsNoDetailNew}>
                                {children}
                            </div>
                        )}

                        {publication?.condition === "used" &&
                            <div className={styles.buttonContainer}>
                                <div className={styles.buttonRow}>
                                    <span className={styles.buttonFlex}>
                                        <ButtonShared
                                            onPress={() => { }}
                                            title={windowSize.width < 420 ? "Negociar" : "Negociar precio"}
                                            publicationTransparent
                                            id="offerPublicationButton"
                                            size="small"
                                            disabled

                                        />
                                    </span>
                                    <span className={styles.buttonFlex}>
                                        <ButtonShared
                                            onPress={() => { }}
                                            title="Comprar"
                                            publicationPurple
                                            size="small"
                                            disabled

                                        />
                                    </span>
                                </div>
                                <span className={styles.buttonFlex}>
                                    <ButtonShared
                                        onPress={() => { }}
                                        title="WhatsApp"
                                        iconImg={images.whatsappIcon}
                                        whatsapp
                                        size="small"
                                        disabled
                                    />
                                </span>
                            </div>
                        }
                        {publication?.condition === "new" &&
                            <div className={styles.buttonContainer}>
                                <div className={styles.buttonRow}>
                                    <span className={styles.buttonFlex}>
                                        <ButtonShared
                                            onPress={() => { }}
                                            title="Reservar"
                                            publicationTransparent
                                            id="offerPublicationButton"
                                            size="small"
                                            disabled

                                        />
                                    </span>
                                    <span className={styles.buttonFlex}>
                                        <ButtonShared
                                            onPress={() => { }}
                                            title="Cotizar"
                                            publicationPurple
                                            size="small"
                                            disabled

                                        />
                                    </span>
                                </div>
                                <span className={styles.buttonFlex}>
                                    <ButtonShared
                                        onPress={() => { }}
                                        title="WhatsApp"
                                        iconImg={images.whatsappIcon}
                                        whatsapp
                                        size="small"
                                        disabled

                                    />
                                </span>
                            </div>
                        }
                        {type === "myPublication" && brand && (
                            <PublicationVersionSelector
                                publication={publication}
                                selectedVersion={selectedVersion}
                                setSelectedVersion={setSelectedVersion}
                                type={type}
                                vehicleTypeSelected={vehicleTypeSelected}
                                brand={brand}
                            />
                        )}
                        {type === "myPublication" && brand && (
                            <PublicationDealershipSelector
                                publication={publication}
                                type={type}
                                vehicleTypeSelected={vehicleTypeSelected}
                                brand={brand}
                                brandsDealerships={brandsDealerships}
                                selectedDealership={selectedDealership}
                                setSelectedDealership={setSelectedDealership}
                            />
                        )}
                        <PublicationPromoBanners
                            type={type}
                            vehicleTypeSelected={vehicleTypeSelected}
                            onLinzeCheckClick={onLinzeCheckClick}
                            onAutoFinClick={() => { }}
                            disabled={true}
                        />
                        {type === "salePost" && (
                            <div className={styles.buttonContainer}>
                                {children}
                            </div>
                        )}

                    </div>
                    <PublicationPriceIndicator
                        hasValidSuggestedPrices={type === "salePost" ? hasValidSuggestedPrices : hasValidSuggestedPricesPublication}
                        getPriceCategory={type === "salePost" ? getPriceCategory : getPriceCategoryPublication}
                        getIndicatorPosition={type === "salePost" ? getIndicatorPosition : getIndicatorPositionPublication}
                        getPriceDifferenceMessage={getPriceDifferenceMessage}
                        publication={publication}
                    />
                </div>
            </div>

            <PublicationSimilars
                type={type}
                brand={brand}
                loading={loading}
                publications={publications}
                clickFavorite={clickFavorite}
                sliderLimit={sliderLimit}
            />
        </>
    );
};

export default memo(PublicationDetailRefactored);
